import React from 'react';
import { Grid } from '@material-ui/core';
import { useFormikContext } from 'formik';
import flowRight from 'lodash-es/flowRight';
import { observer } from 'mobx-react';
import { formatDistance, parseISO } from 'date-fns';
import { lookupsJson } from '@ourbranch/lookups';

import { NotificationCard } from 'core/components/notification-card';
import { awsDateToDateFormatter } from 'core/helpers/formatters';
import Section from 'core/components/section';
import CollapsibleCard from 'core/components/collapsible-card';
import { Label } from 'core/components/label';
import { FormField } from 'core/components/form';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import withDatePicker from 'core/components/with-date-picker';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import useStyles from './prior-coverage.styles';

const PriorCoverage = () => {
  const classes = useStyles();

  const {
    offer: offerStore,
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();

  const { values } = useFormikContext();

  const { global } = values;
  const { canEditPriorCarrierDetails, isAgency } = useSession();
  const { inOfferEditRoute } = useRouteHelper();

  const priorCars =
    (inOfferEditRoute ? global?.priorCars : policyStore?.policy?.offer?.quote?.global?.priorCars) || null;

  const priorDrivers =
    (inOfferEditRoute ? global?.priorDrivers : policyStore?.policy?.offer?.quote?.global?.priorDrivers) || null;

  const hasOfferNoPriorAutoRejection =
    inOfferEditRoute &&
    offerStore?.offer.rejections?.some((rejection) => rejection.value === 50007 || rejection.value === 50038);
  const canEdit = canEditPriorCarrierDetails || hasOfferNoPriorAutoRejection;
  const showNotification = !global.receivedCVData || (global.receivedCVData && !global.hadInforceCVPolicy);

  return (
    <Section title="Information from Prior Carrier" type="SubSection">
      <CollapsibleCard
        content={
          <Label type="infoSubtitle" style={{ display: 'inline', marginRight: 8 }}>
            Coverage Verifier Report
          </Label>
        }
        initialOpen={hasOfferNoPriorAutoRejection}
      >
        <Grid container justifyContent="space-around" className={classes.container}>
          {showNotification && (
            <NotificationCard type="infoGreen" iconSize="icon16" className={classes.notification}>
              {inOfferEditRoute
                ? 'Branch will require proof of prior coverage after this policy is bound.'
                : !isAgency
                  ? 'Note: Some prior coverage data was missing from CV, requiring the "Need Proof of Prior" onboarding Task.'
                  : null}
            </NotificationCard>
          )}
          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            alignItems="flex-start"
            className={classes.section}
          >
            <Grid item xs={12}>
              <Label type="formSubTitleLight">Policy information</Label>
            </Grid>

            {canEdit ? (
              <FormField
                name="global.currentAutoCarrier"
                id="global.currentAutoCarrier"
                type="string"
                label={inOfferEditRoute ? 'Current Carrier' : 'Prior Carrier'}
                xs={4}
                mode="light"
              />
            ) : (
              <FormField
                name="global.currentAutoCarrier"
                id="global.currentAutoCarrier"
                type="value"
                label={inOfferEditRoute ? 'Current Carrier' : 'Prior Carrier'}
                value={global?.currentAutoCarrier || 'N/A'}
              />
            )}

            {canEdit ? (
              <FormField
                name="global.continuousAutoCoverageStartDate"
                id="global.continuousAutoCoverageStartDate"
                type="date"
                label={inOfferEditRoute ? 'Auto Coverage Start Date' : 'Prior Coverage Start Date'}
                xs={4}
                mode="light"
              />
            ) : (
              <FormField
                name="global.continuousAutoCoverageStartDate"
                id="global.continuousAutoCoverageStartDate"
                type="value"
                label={inOfferEditRoute ? 'Auto Coverage Start Date' : 'Prior Coverage Start Date'}
                xs={16}
                value={
                  global?.continuousAutoCoverageStartDate
                    ? awsDateToDateFormatter(global?.continuousAutoCoverageStartDate)
                    : 'N/A'
                }
              />
            )}

            {canEdit ? (
              <FormField
                name="global.mostRecentAutoInsuranceCoverageEndDate"
                id="global.mostRecentAutoInsuranceCoverageEndDate"
                type="date"
                label="Coverage Expiration Date"
                xs={4}
                mode="light"
              />
            ) : (
              <FormField
                name="global.mostRecentAutoInsuranceCoverageEndDate"
                id="global.mostRecentAutoInsuranceCoverageEndDate"
                type="value"
                label="Coverage Expiration Date"
                value={
                  global?.mostRecentAutoInsuranceCoverageEndDate
                    ? awsDateToDateFormatter(global?.mostRecentAutoInsuranceCoverageEndDate)
                    : 'N/A'
                }
                xs={16}
              />
            )}

            <FormField
              name="global.continuousDuration"
              id="global.continuousDuration"
              type="value"
              label="Continuous Policy Duration"
              xs={4}
              value={
                global?.continuousAutoCoverageStartDate && typeof global?.continuousAutoCoverageStartDate === 'string'
                  ? formatDistance(new Date(), parseISO(global?.continuousAutoCoverageStartDate)).toUpperCase()
                  : 'N/A'
              }
              fast={false}
            />
          </Grid>

          <Grid
            container
            justifyContent="space-between"
            alignItems="flex-start"
            className={classes.section}
            spacing={2}
          >
            <Grid item xs={12}>
              <Label type="formSubTitleLight">Auto policy coverage</Label>
            </Grid>

            {canEdit ? (
              <FormField
                name="global.priorIndividualBILimit"
                id="global.priorIndividualBILimit"
                type="select"
                label="Prior Bodily Injury Limit (Individual)"
                options={lookupsJson.priorIndividualBILimit}
                mode="light"
                xs={6}
              />
            ) : (
              <FormField
                name="global.priorIndividualBILimit"
                id="global.priorIndividualBILimit"
                type="value"
                label="Prior Bodily Injury Limit (Individual)"
                value={
                  global?.priorIndividualBILimit?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ||
                  'N/A'
                }
                xs={16}
              />
            )}
            {canEdit ? (
              <FormField
                name="global.priorOccurrenceBILimit"
                id="global.priorOccurrenceBILimit"
                type="select"
                label="Prior Bodily Injury Limit (Occurrence)"
                options={lookupsJson.priorBIOccurrenceLimit}
                xs={6}
                mode="light"
              />
            ) : (
              <FormField
                name="global.priorOccurrenceBILimit"
                id="global.priorOccurrenceBILimit"
                type="value"
                label="Prior Bodily Injury Limit (Occurrence)"
                value={
                  global?.priorOccurrenceBILimit?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ||
                  'N/A'
                }
                xs={16}
              />
            )}
            {canEdit ? (
              <FormField
                name="global.priorUmIndividualBodilyInjury"
                id="global.priorUmIndividualBodilyInjury"
                type="select"
                label="Prior Uninsured Motorist BI (Individual)"
                options={lookupsJson.priorUmIndividualBodilyInjury}
                xs={6}
                mode="light"
              />
            ) : (
              <FormField
                name="global.priorUmIndividualBodilyInjury"
                id="global.priorUmIndividualBodilyInjury"
                type="value"
                label="Uninsured Motorist BI (Individual)"
                value={
                  global?.priorUmIndividualBodilyInjury && global?.priorUmOccurrenceBodilyInjury
                    ? `${global.priorUmIndividualBodilyInjury?.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD'
                      })}/${global.priorUmOccurrenceBodilyInjury?.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD'
                      })}`
                    : 'N/A'
                }
                xs={16}
              />
            )}
            {canEdit ? (
              <FormField
                name="global.priorUmOccurrenceBodilyInjury"
                id="global.priorUmOccurrenceBodilyInjury"
                type="select"
                label="Prior Uninsured Motorist BI (Occurrence)"
                options={lookupsJson.priorUmOccurrenceBodilyInjury}
                xs={6}
                mode="light"
              />
            ) : (
              <FormField
                name="global.priorUmOccurrenceBodilyInjury"
                id="global.priorUmOccurrenceBodilyInjury"
                type="value"
                label="Uninsured Motorist BI (Occurrence)"
                value={
                  global?.priorUmIndividualBodilyInjury && global?.priorUmOccurrenceBodilyInjury
                    ? `${global.priorUmIndividualBodilyInjury?.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD'
                      })}/${global.priorUmOccurrenceBodilyInjury?.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD'
                      })}`
                    : 'N/A'
                }
                xs={16}
              />
            )}
          </Grid>

          <Grid container className={classes.section}>
            <Grid item xs={12}>
              <Label type="formSubTitleLight">
                {inOfferEditRoute ? 'Drivers on current policy' : 'Drivers on prior policy'}
              </Label>
            </Grid>
            {priorDrivers ? (
              priorDrivers.map((driver) => (
                <Grid
                  container
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  key={`${driver.firstName}${driver.lastName}`}
                >
                  <div className={classes.column}>
                    <FormField
                      id="driverFirstName"
                      name="driverFirstName"
                      type="value"
                      label="First Name"
                      value={driver?.firstName}
                    />
                  </div>
                  <FormField
                    id="driverLastName"
                    name="driverLastName"
                    type="value"
                    label="Last Name"
                    value={driver?.lastName}
                  />
                </Grid>
              ))
            ) : (
              <Grid container justifyContent="flex-start" alignItems="flex-start">
                <div className={classes.column}>
                  <FormField name="firstName" type="value" label="First Name" value="N/A" xs={16} />
                </div>
                <FormField name="lastName" type="value" label="Last Name" value="N/A" xs={16} />
              </Grid>
            )}
          </Grid>

          <Grid container className={classes.section}>
            <Grid item xs={12}>
              <Label type="formSubTitleLight">
                {inOfferEditRoute ? 'Cars on current policy' : 'Cars on prior policy'}
              </Label>
            </Grid>
            {priorCars ? (
              priorCars.map((vehicle, index) => (
                <Grid
                  container
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  className={classes.carDetails}
                  key={vehicle.VIN}
                >
                  <FormField
                    name={`${index}carVin`}
                    type="value"
                    label="Car/Car Vin"
                    value={`${vehicle?.year} ${vehicle?.make} ${vehicle?.model} / ${vehicle?.VIN}`}
                    xs={16}
                  />
                  <Grid container item spacing={2} justifyContent="space-between">
                    <FormField
                      name={`${index}carCollision`}
                      type="value"
                      label="Collision Deductible"
                      value={
                        vehicle?.deductibleCollision
                          ? `$${Number(vehicle.deductibleCollision).toLocaleString('en-US')}`
                          : 'No Coverage'
                      }
                      xs={16}
                    />
                    <FormField
                      name={`${index}carComprehensive`}
                      type="value"
                      label="Comprehensive Deductible"
                      value={
                        vehicle?.deductibleComprehensive
                          ? `$${Number(vehicle.deductibleComprehensive).toLocaleString('en-US')}`
                          : 'No Coverage'
                      }
                      xs={16}
                    />
                    <FormField
                      name={`${index}carRoadsideAssistance`}
                      type="value"
                      label="Roadside Assistance"
                      value={vehicle.roadsideAssistance || 'N/A'}
                      xs={16}
                    />
                    <FormField
                      name={`${index}carLimitRental`}
                      type="value"
                      label="Rental Reimbursement"
                      value={vehicle.limitRental !== null ? `${vehicle.limitRental}` : 'N/A'}
                      xs={16}
                    />
                  </Grid>
                </Grid>
              ))
            ) : (
              <Grid container justifyContent="flex-start" alignItems="flex-start">
                <div className={classes.column}>
                  <FormField name="Car" type="value" label="Car" value="N/A" xs={16} />
                </div>
                <FormField name="car vin" type="value" label="Car VIN" value="N/A" xs={16} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </CollapsibleCard>
    </Section>
  );
};

export default flowRight(withDatePicker, observer)(PriorCoverage);
